"use client";

import { Button, LoadingSpinner } from "@west-tennessee-consulting/ui";
import { ArrowLeft } from "lucide-react";
import { AttachmentSelectionList } from "./AttachmentSelectionList";
import { ClientFolderPicker } from "./ClientFolderPicker";
import { CreateDriveFolderForm } from "./CreateDriveFolderForm";
import { SaveAttachmentsDestination } from "./SaveAttachmentsDestination";
import { SaveAttachmentsResults } from "./SaveAttachmentsResults";
import { SelectedClientFolder } from "./SelectedClientFolder";
import type { useSaveAttachmentsToGoogleDriveState } from "./useSaveAttachmentsToGoogleDriveState";

interface SaveAttachmentsModalBodyProps {
  state: ReturnType<typeof useSaveAttachmentsToGoogleDriveState>;
}

export function SaveAttachmentsModalBody({ state }: SaveAttachmentsModalBodyProps) {
  if (state.uploadResults) {
    return <SaveAttachmentsResults uploadResults={state.uploadResults} />;
  }

  if (state.attachmentsError) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-lg">
        <p className="text-sm text-red-800 dark:text-red-200">
          Error loading attachments:{" "}
          {state.attachmentsError instanceof Error
            ? state.attachmentsError.message
            : "Attachments could not be loaded"}
        </p>
      </div>
    );
  }

  if (state.isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner className="h-5 w-5 mr-2" />
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    );
  }

  const browsing = state.destinationMode === "folder";

  return (
    <>
      <AttachmentSelectionList
        attachments={state.attachments}
        onDeselectAll={state.deselectAllAttachments}
        onSelectAll={state.selectAllAttachments}
        onToggleAttachment={state.toggleAttachmentSelection}
        selectedAttachmentIds={state.selectedAttachmentIds}
      />

      {!browsing && (
        <SaveAttachmentsDestination
          companyName={state.companyName}
          onBrowse={() => state.setDestinationMode("folder")}
        />
      )}

      {browsing && (
        <>
          {/*
            Only offered when there IS a company default to go back to. With no
            CRM match the modal opened straight into the picker, so a "back"
            would land on an empty destination the operator cannot act on.
          */}
          {state.companyId && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-1 text-xs"
              onClick={() => {
                state.setDestinationMode("company");
                state.setSelectedClient(null);
                state.setShowCreateFolderForm(false);
              }}
            >
              <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
              Back to {state.companyName ?? "the customer's folder"}
            </Button>
          )}

          {!state.companyId && (
            <p className="text-xs text-muted-foreground">
              We couldn&apos;t match this sender to a company, so pick where these files
              should go.
            </p>
          )}

          {!state.hasAnyDriveAccess && !state.statusLoading ? (
            <div className="p-4 bg-yellow-50 dark:bg-yellow-950/40 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                Browsing Drive folders needs your own Google account connected.{" "}
                <a href="/crm/calendar/setup" className="font-semibold underline">
                  Set up Google Drive access
                </a>
                {state.companyId ? (
                  <>
                    {" "}
                    — or go back and save to {state.companyName ?? "the customer's folder"},
                    which doesn&apos;t need it.
                  </>
                ) : null}
              </p>
            </div>
          ) : (
            <>
              <ClientFolderPicker
                clientFolders={state.clientFolders}
                isRefreshing={state.isRefreshing}
                listingLoading={state.listingLoading}
                onRefreshFolders={state.handleRefreshFolders}
                onSelectClient={state.setSelectedClient}
                selectedClient={state.selectedClient}
              />

              <CreateDriveFolderForm
                hasAnyDriveAccess={state.hasAnyDriveAccess}
                isPending={state.createFolderMutation.isPending}
                newFolderName={state.newFolderName}
                onCancel={() => {
                  state.setShowCreateFolderForm(false);
                  state.setNewFolderName("");
                }}
                onNewFolderNameChange={state.setNewFolderName}
                onSubmit={state.handleCreateFolderSubmit}
                onToggleForm={() => state.setShowCreateFolderForm(!state.showCreateFolderForm)}
                showCreateFolderForm={state.showCreateFolderForm}
              />

              <SelectedClientFolder
                onClear={() => state.setSelectedClient(null)}
                selectedClient={state.selectedClient}
              />
            </>
          )}
        </>
      )}
    </>
  );
}
